import React from 'react';
import styles from '../../styles.css';
import measureGroupBarStyles from './styles.css';
import QuickSelectDropdown from '../QuickSelectDropdown/index.jsx';

import { isAllChildrensSelected, isSearchedPhrase } from '../../utils';
import HighlightText from '../../HighlightText.jsx';
import { useTranslation } from 'react-i18next';

const CategoryQuickSelect = ({
	subjectColor,
	subjectId,
	subjectCounters,
	subject,
	selectedSubjects,
	addSelectedSubject,
	expandedMeasureBars,
	setExpandedMeasureBars,
	nestedListsToShow,
	showNestedLists,
	quickSelectionDescription,
}) => {
	let allSubCategories = [];
	let allIndicators = [];
	let allIndicatorsWithSubSubIndicators = [];
	let allSubIndicators = [];
	let allSubSubIndicators = [];
	const { t } = useTranslation();

	const getDependantChildrens = (list) => {
		list?.childrens?.forEach((child) => {
			if (child.childrens) {
				getDependantChildrens(child);
			}
			if (child.itemType === 'SubCategories') {
				allSubCategories.push(child.id);
			}
			if (child.itemType === 'Indicators') {
				allIndicators.push(child.id);
				if (child.childrens) {
					child.childrens.forEach((subChild) => {
						if (subChild.childrens && subChild.childrens.length) {
							allIndicatorsWithSubSubIndicators.push(child.id);
						}
					});
				}
			}
			if (child.itemType === 'SubIndicators') {
				allSubIndicators.push(child.id);
			}
			if (child.itemType === 'SubSubIndicators') {
				allSubSubIndicators.push(child.id);
			}
		});
	};

	getDependantChildrens(subject);

	const optionMap = {
		subCategories: {
			addIds: allSubCategories,
			removeIds: allSubCategories,
		},
		indicators: {
			addIds: allIndicators,
			removeIds: allIndicators,
		},
		subIndicators: {
			addIds: allSubIndicators,
			removeIds: allSubIndicators,
		},
		subSubIndicators: {
			addIds: allSubSubIndicators,
			removeIds: allSubSubIndicators,
		},
	};

	const options = [
		{
			key: 'subCategories',
			label: subject.isCV ? t('Sub-sections') : t('Sub-categories'),
			count: allSubCategories.length,
		},
		{ key: 'indicators', label: t('Indicators'), count: allIndicators.length },
		{ key: 'subIndicators', label: t('Sub-indicators'), count: allSubIndicators.length },
		{ key: 'subSubIndicators', label: t('Sub-sub-indicators'), count: allSubSubIndicators.length },
	];

	const initialCheckedKeys = new Set();
	options.forEach((option) => {
		const map = optionMap[option.key];
		if (map && map.addIds.length > 0 && isAllChildrensSelected(map.addIds, selectedSubjects)) {
			initialCheckedKeys.add(option.key);
		}
	});

	const handleConfirm = (checkedKeys, uncheckedKeys) => {
		const subjectsToAdd = new Set();
		const subjectsToRemove = new Set();

		checkedKeys.forEach((key) => {
			if (!initialCheckedKeys.has(key)) {
				optionMap[key]?.addIds.forEach((id) => subjectsToAdd.add(id));
			}
		});

		uncheckedKeys.forEach((key) => {
			optionMap[key]?.removeIds.forEach((id) => subjectsToRemove.add(id));
		});

		subjectsToAdd.forEach((id) => subjectsToRemove.delete(id));

		if (subjectsToAdd.size > 0 || subjectsToRemove.size > 0) {
			addSelectedSubject({
				subjectsGroup: [...subjectsToAdd],
				subjectsToRemove: [...subjectsToRemove],
				selectedSubjects,
			});
		}

		const nextNestedListsToShow = new Set(nestedListsToShow);

		if (uncheckedKeys.has('subIndicators')) {
			nextNestedListsToShow.forEach((s) => {
				if (!allIndicators.includes(s)) {
					nextNestedListsToShow.delete(s);
				}
			});
		}
		if (uncheckedKeys.has('subSubIndicators')) {
			nextNestedListsToShow.forEach((s) => {
				if (!allSubIndicators.includes(s) && !allIndicatorsWithSubSubIndicators.includes(s)) {
					nextNestedListsToShow.delete(s);
				}
			});
		}

		if (checkedKeys.has('subIndicators') && !initialCheckedKeys.has('subIndicators')) {
			allIndicators.forEach((indicator) => nextNestedListsToShow.add(indicator));
		}
		if (checkedKeys.has('subSubIndicators') && !initialCheckedKeys.has('subSubIndicators')) {
			allIndicatorsWithSubSubIndicators.forEach((indicator) => nextNestedListsToShow.add(indicator));
			allSubIndicators.forEach((indicator) => nextNestedListsToShow.add(indicator));
		}

		showNestedLists([...nextNestedListsToShow]);
	};

	return (
		<>
			{subjectCounters[subjectId] > 0 && (
				<div
					className={`${measureGroupBarStyles.selectionCounter}`}
					onClick={() => {
						const listOfSubjectsToExpand = [];
						const getDependantChildrens = (list) => {
							list.childrens.forEach((child) => {
								if (child.childrens) {
									getDependantChildrens(child);
								}
								if (selectedSubjects.includes(child.id) && !listOfSubjectsToExpand.includes(child.parent)) {
									listOfSubjectsToExpand.push(child.parent);
									if (child.itemType === 'SubSubIndicators') {
										listOfSubjectsToExpand.push(child.ancestors[child.ancestors.length - 2]);
									}
								}
							});
						};

						getDependantChildrens(subject);

						listOfSubjectsToExpand.push(subject.id);
						if (!expandedMeasureBars.includes(subject.id)) {
							setExpandedMeasureBars([...expandedMeasureBars, subject.id]);
							showNestedLists([...nestedListsToShow, ...listOfSubjectsToExpand]);
						} else {
							const collapseExpandedLists = nestedListsToShow.filter(
								(subjectId) => !listOfSubjectsToExpand.includes(subjectId),
							);
							setExpandedMeasureBars(expandedMeasureBars.filter((bar) => bar !== subject.id));
							showNestedLists(collapseExpandedLists);
						}
					}}
					title={t('Click to show selected measures')}
				>
					{subjectCounters[subjectId]}
				</div>
			)}
			{subject.id === 'CITVOICE' ? (
				<span
					onClick={() => {
						const modal = UIkit.modal('#modalCitvoiceInfo');
						modal.center = true;
						modal.show();
					}}
					className={measureGroupBarStyles.citvoiceInfoIcon}
					title="Info"
				>
					{/* eslint-disable-next-line i18next/no-literal-string */}
					{'ⓘ'}
				</span>
			) : null}
			<div className={`${measureGroupBarStyles.separator} ${styles.visibleMedium}`} />
			<div className={`${measureGroupBarStyles.measureQuick} ${styles.visibleMedium}`}>
				<QuickSelectDropdown
					options={options}
					initialCheckedKeys={initialCheckedKeys}
					onConfirm={handleConfirm}
					align="right"
					hideLabelOnMobile
				/>
				{quickSelectionDescription ? (
					<img
						onClick={() => quickSelectionDescription.onClick()}
						className={`${styles.informationIcon} ${measureGroupBarStyles.measureQuickInfoIcon}`}
						src="/img/icons/info_icon.svg"
						alt="Info"
						width="17"
						height="17"
					/>
				) : null}
			</div>
			<div className={measureGroupBarStyles.filler} />
		</>
	);
};

export default function MeasureGroupBar({
	subject,
	isSimplifiedView,
	selectedSubjects,
	onSubjectSelect,
	queryParams,
	nestedListsToShow,
	showNestedLists,
	subjectCounters,
	selectedGroupsOfSubjects,
	onSelectAllSubjects,
	addSelectedSubject,
	removeSelectedSubject,
	expandedMeasureBars,
	setExpandedMeasureBars,
	searchPhrase,
	onSubjectChange,
	quickSelectionDescription,
}) {
	let disableExpandButton = false;

	let subjectsGroup = [];

	const getSubjectChildrens = (childrens) => {
		childrens.forEach((child) => {
			if (child.childrens) {
				getSubjectChildrens(child.childrens);
			}
			subjectsGroup.push(child.fullName);
		});
	};

	subject.childrens ? getSubjectChildrens(subject.childrens) : (subjectWithNoChildren = true);

	const hasSelectedDescendant = (() => {
		if (isSimplifiedView) return false;
		const checkDescendants = (item) => {
			if (!item.childrens) return false;
			return item.childrens.some((child) => selectedSubjects.includes(child.id) || checkDescendants(child));
		};
		return checkDescendants(subject);
	})();

	subjectsGroup.forEach((children) => {
		if (
			isSearchedPhrase({
				subjectName: children,
				phrase: searchPhrase,
			})
		) {
			disableExpandButton = true;
		}
	});

	return (
		<div className={styles.measureCollapse}>
			<h5
				className={`${measureGroupBarStyles.measureName} ${
					!isSimplifiedView && selectedSubjects.includes(subject.id) ? measureGroupBarStyles.selectedItem : ''
				}`}
			>
				<a
					href={`/measures/${subject.id.toLowerCase()}.html`}
					className={`${
						isSearchedPhrase({
							subjectName: subject.fullName,
							phrase: searchPhrase,
						})
							? styles.highlightSearchedWord
							: ''
					} ${styles.level0} ${
						!isSimplifiedView && selectedSubjects.includes(subject.id) ? measureGroupBarStyles.selected : ''
					}
						${isSimplifiedView ? measureGroupBarStyles.measureTitle : ''}`}
					onClick={(e) => {
						if (isSimplifiedView && onSubjectChange) {
							e.preventDefault();
							onSubjectChange(subject.id);
						}
						if (!isSimplifiedView) {
							e.preventDefault();
							onSubjectSelect(subject.id);
						}
					}}
				>
					<img
						className={`${styles.measureIcon} ${measureGroupBarStyles.measureIcon}`}
						src={`/img/icons/${subject.id}.svg`}
						alt={subject.fullName}
					/>
					<span className={measureGroupBarStyles.measureLabel}>
						<HighlightText text={subject.fullName} phrase={searchPhrase} />
					</span>
					{!isSimplifiedView && selectedSubjects.includes(subject.id) && (
						<span
							className={`${styles.deselectX} ${measureGroupBarStyles.measureDeselect}`}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								onSubjectSelect(subject.id);
							}}
						>
							<svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
							</svg>
						</span>
					)}
				</a>
			</h5>
			{!isSimplifiedView ? (
				<CategoryQuickSelect
					subjectCounters={subjectCounters}
					subjectId={subject.id}
					subjectColor={subject.subjectColor}
					subject={subject}
					selectedSubjects={selectedSubjects}
					addSelectedSubject={addSelectedSubject}
					expandedMeasureBars={expandedMeasureBars}
					setExpandedMeasureBars={setExpandedMeasureBars}
					nestedListsToShow={nestedListsToShow}
					showNestedLists={showNestedLists}
					quickSelectionDescription={quickSelectionDescription}
				/>
			) : null}

			{!nestedListsToShow.includes(subject.id) ? (
				<span
					className={`${styles.expandIcon} ${measureGroupBarStyles.expandButton} ${isSimplifiedView ? measureGroupBarStyles.floatRight : ''} ${hasSelectedDescendant ? measureGroupBarStyles.expandButtonActive : ''}`}
					onClick={() => {
						showNestedLists((displayedRows) => [...displayedRows, subject.id]);
					}}
				>
					{/* eslint-disable-next-line i18next/no-literal-string */}
					{'+'}
				</span>
			) : (
				<span
					className={`${styles.expandIcon} ${measureGroupBarStyles.expandButton} ${isSimplifiedView ? measureGroupBarStyles.floatRight : ''} ${disableExpandButton ? measureGroupBarStyles.disable : ''} ${hasSelectedDescendant ? measureGroupBarStyles.expandButtonActive : ''}`}
					onClick={() => {
						if (disableExpandButton) {
							return;
						}
						showNestedLists((displayedRows) => displayedRows.filter((row) => row !== subject.id));
					}}
				>
					<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M2.5 6H9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
					</svg>
				</span>
			)}
		</div>
	);
}
